import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ErrorService } from '../shared/services/error.service';
import {
  AsaasCharge,
  AsaasChargeBatch,
  AsaasCustomer,
  AsaasPaymentResponse,
  BBBoleto,
  BBPix,
  BlobUploadResponse,
  CancelBoletosBatchResponse
} from '../models/Payment';

@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  private readonly baseUrl = environment.microservices.payment;

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  private buildUrl(endpoint: string): string {
    const [base, query] = this.baseUrl.split('?');
    return `${base}${endpoint}${query ? '?' + query : ''}`;
  }

  // ==========================================
  // 1. Asaas Payments
  // ==========================================

  // --- Charges ---
  // POST {payment}/payments/asaas/charges?code=... (cobrança avulsa PIX/Boleto 1x)
  createAsaasCharge(charge: AsaasCharge): Observable<AsaasCharge> {
    return this.http.post<AsaasCharge>(this.buildUrl('/payments/asaas/charges'), charge)
      .pipe(catchError(err => this.handleError(err, 'Erro ao criar cobrança Asaas')));
  }

  createAsaasChargeWithCustomerValidation(
    customerData: AsaasCustomer, 
    chargeData: Omit<AsaasCharge, 'customerId'>
  ): Observable<AsaasCharge> {
    if (!customerData.cpfCnpj) {
      return throwError(() => new Error('CPF/CNPJ é obrigatório para validação de cliente Asaas.'));
    }

    const normalizedCpfCnpj = customerData.cpfCnpj.replace(/\D/g, '');

    return this.listAsaasCustomers({ cpfCnpj: normalizedCpfCnpj }).pipe(
      switchMap(response => {
        const existingCustomer = response.data && response.data.length > 0 ? response.data[0] : null;
        
        if (existingCustomer) {
          return of(existingCustomer);
        } else {
          return this.createAsaasCustomer({ ...customerData, cpfCnpj: normalizedCpfCnpj });
        }
      }),
      switchMap(customer => {
        if (!customer || !customer.id) {
           return throwError(() => new Error('Falha ao obter ID do cliente Asaas.'));
        }
        const charge: AsaasCharge = {
          ...chargeData,
          customerId: customer.id
        };
        return this.createAsaasCharge(charge);
      }),
      catchError(err => this.handleError(err, 'Erro no fluxo de criação de cobrança com validação de cliente'))
    );
  }

  // POST {payment}/payments/asaas/boletos/batch?code=... (lote de boletos: caução parcelada, plano semanal)
  createAsaasChargeBatch(charges: AsaasChargeBatch[]): Observable<any> {
    return this.http.post<any>(this.buildUrl('/payments/asaas/boletos/batch'), charges)
      .pipe(catchError(err => this.handleError(err, 'Erro ao criar lote de boletos Asaas')));
  }

  createAsaasChargeRaw(payload: any): Observable<any> {
    return this.http.post<any>(this.buildUrl('/payments/asaas/charges/raw'), payload)
      .pipe(catchError(err => this.handleError(err, 'Erro ao criar cobrança (raw) Asaas')));
  }

  getAsaasCharge(chargeId: string): Observable<AsaasCharge> {
    return this.http.get<AsaasCharge>(this.buildUrl(`/payments/asaas/charges/${chargeId}`))
      .pipe(catchError(err => this.handleError(err, 'Erro ao obter cobrança Asaas')));
  }

  /**
   * Cancela múltiplas cobranças Asaas em lote.
   * POST /payments/asaas/boletos/cancel-batch
   * Cada cobrança é processada individualmente; algumas podem falhar sem afetar as demais.
   */
  cancelBoletosBatch(chargeIds: string[]): Observable<CancelBoletosBatchResponse> {
    return this.http.post<CancelBoletosBatchResponse>(
      this.buildUrl('/payments/asaas/boletos/cancel-batch'),
      { chargeIds }
    ).pipe(catchError(err => this.handleError(err, 'Erro ao cancelar boletos em lote no Asaas')));
  }

  /**
   * Cancela uma cobrança no Asaas (DELETE /payments/asaas/charges/{chargeId})
   * @param chargeId ID da cobrança no Asaas
   * @returns Dados da cobrança cancelada
   */
  cancelAsaasCharge(chargeId: string): Observable<AsaasCharge> {
    return this.http.delete<AsaasCharge>(this.buildUrl(`/payments/asaas/charges/${chargeId}`))
      .pipe(catchError(err => this.handleError(err, 'Erro ao cancelar cobrança Asaas')));
  }

  listAsaasCharges(filters?: {
    offset?: number;
    limit?: number;
    customer?: string;
    status?: string;
    externalReference?: string;
    invoiceNumber?: string;
  }): Observable<any> {
    let params = new HttpParams();
    if (filters) {
      if (filters.offset !== undefined) params = params.set('offset', filters.offset.toString());
      if (filters.limit !== undefined) params = params.set('limit', filters.limit.toString());
      if (filters.customer) params = params.set('customer', filters.customer);
      if (filters.status) params = params.set('status', filters.status);
      if (filters.externalReference) params = params.set('externalReference', filters.externalReference);
      if (filters.invoiceNumber) params = params.set('invoiceNumber', filters.invoiceNumber);
    }
    return this.http.get<any>(this.buildUrl('/payments/asaas/charges'), { params })
      .pipe(catchError(err => this.handleError(err, 'Erro ao listar cobranças Asaas')));
  }

  createAsaasCustomer(customer: AsaasCustomer): Observable<AsaasCustomer> {
    return this.http.post<AsaasCustomer>(this.buildUrl('/payments/asaas/customers'), customer)
      .pipe(catchError(err => this.handleError(err, 'Erro ao criar cliente Asaas')));
  }

  createAsaasCustomerRaw(payload: any): Observable<any> {
    return this.http.post<any>(this.buildUrl('/payments/asaas/customers/raw'), payload)
      .pipe(catchError(err => this.handleError(err, 'Erro ao criar cliente (raw) Asaas')));
  }

  listAsaasCustomers(filters?: {
    offset?: number;
    limit?: number;
    cpfCnpj?: string;
    email?: string;
    name?: string;
  }): Observable<any> {
    let params = new HttpParams();
    if (filters) {
      if (filters.offset !== undefined) params = params.set('offset', filters.offset.toString());
      if (filters.limit !== undefined) params = params.set('limit', filters.limit.toString());
      if (filters.cpfCnpj) params = params.set('cpfCnpj', filters.cpfCnpj);
      if (filters.email) params = params.set('email', filters.email);
      if (filters.name) params = params.set('name', filters.name);
    }
    return this.http.get<any>(this.buildUrl('/payments/asaas/customers'), { params })
      .pipe(catchError(err => this.handleError(err, 'Erro ao listar clientes Asaas')));
  }

  getAsaasCustomer(customerId: string): Observable<AsaasCustomer> {
    return this.http.get<AsaasCustomer>(this.buildUrl(`/payments/asaas/customers/${customerId}`))
      .pipe(catchError(err => this.handleError(err, 'Erro ao obter cliente Asaas')));
  }

  /**
   * Remove (cancela) **todas** as cobranças do cliente que batam com o status informado.
   * ⚠️ Não usar no congelamento — use cancelamento por lista de IDs (`cancelSpecificAsaasBoletosByChargeIds`).
   * Ex.: DELETE .../customers/{id}/charges?status=PENDING cancela **todos** os pendentes do cliente.
   */
  deleteAsaasCustomerCharges(customerId: string, status?: string): Observable<any> {
    let params = new HttpParams();
    if (status) {
      params = params.set('status', status);
    }
    return this.http.delete<any>(this.buildUrl(`/payments/asaas/customers/${customerId}/charges`), { params })
      .pipe(catchError(err => this.handleError(err, 'Erro ao cancelar cobranças Asaas do cliente')));
  }

  /**
   * Pós-cancelamento de contrato (mesmo fluxo da lista de contratos e B2B):
   * localiza cliente Asaas pelo CPF/CNPJ, verifica se há cobranças vencidas (OVERDUE) e
   * cancela apenas cobranças em aberto (PENDING). Boletos vencidos permanecem no Asaas.
   */
  cancelPendingAsaasChargesAfterContractCancel(
    cpfCnpj: string
  ): Observable<{ hasOverdue: boolean; customerFound: boolean }> {
    const digits = (cpfCnpj || '').replace(/\D/g, '');
    if (!digits || (digits.length !== 11 && digits.length !== 14)) {
      return of({ hasOverdue: false, customerFound: false });
    }
    return this.listAsaasCustomers({ cpfCnpj: digits }).pipe(
      switchMap((res) => {
        const customerId = res?.data?.[0]?.id as string | undefined;
        if (!customerId) {
          return of({ hasOverdue: false, customerFound: false });
        }
        return this.listAsaasCharges({ customer: customerId, status: 'OVERDUE', limit: 1 }).pipe(
          switchMap((chargesRes) => {
            const hasOverdue = (chargesRes?.totalCount || 0) > 0;
            return this.deleteAsaasCustomerCharges(customerId, 'PENDING').pipe(
              map(() => ({ hasOverdue, customerFound: true }))
            );
          })
        );
      })
    );
  }

  /**
   * Cancela **somente** os boletos/cobranças cujos IDs foram enviados.
   * POST /payments/asaas/boletos/cancel-batch — corpo `{ chargeIds: [...] }`.
   * Não cancela outras cobranças do cliente (diferente de `deleteAsaasCustomerCharges`).
   */
  cancelSpecificAsaasBoletosByChargeIds(chargeIds: string[]): Observable<any> {
    if (!chargeIds?.length) {
      return of({ success: true });
    }
    return this.http
      .post<any>(this.buildUrl('/payments/asaas/boletos/cancel-batch'), { chargeIds })
      .pipe(catchError(err => this.handleError(err, 'Erro ao cancelar boletos selecionados no Asaas')));
  }

  /** @deprecated Use `cancelSpecificAsaasBoletosByChargeIds` (mesmo endpoint, nome mais claro). */
  cancelAsaasBoletosBatch(chargeIds: string[]): Observable<any> {
    return this.cancelSpecificAsaasBoletosByChargeIds(chargeIds);
  }

  /**
   * Congelamento: localiza boletos **somente PENDING**, vencimento no período de congelamento,
   * e chama cancel-batch **apenas com esses chargeIds** (nunca DELETE em massa por cliente).
   */
  cancelPendingBoletosInFreezeWindow(
    cpfCnpjDigits: string,
    freezeStartUtc: string,
    freezeEndUtc: string
  ): Observable<{ cancelledCount: number }> {
    const startDay = this.isoUtcToYyyyMmDd(freezeStartUtc);
    const endDay = this.isoUtcToYyyyMmDd(freezeEndUtc);
    if (!startDay || !endDay || startDay > endDay) {
      return of({ cancelledCount: 0 });
    }
    if (!cpfCnpjDigits || (cpfCnpjDigits.length !== 11 && cpfCnpjDigits.length !== 14)) {
      return of({ cancelledCount: 0 });
    }

    return this.listAsaasCustomers({ cpfCnpj: cpfCnpjDigits }).pipe(
      switchMap((custRes: any) => {
        const customerId = custRes?.data?.[0]?.id as string | undefined;
        if (!customerId) {
          return of({ cancelledCount: 0 });
        }
        return this.listAsaasCharges({
          customer: customerId,
          status: 'PENDING',
          limit: 500
        }).pipe(
          switchMap((chargesRes: any) => {
            const rows: AsaasPaymentResponse[] = chargesRes?.data || chargesRes?.Data || [];
            const pendingBoletoInWindow = rows.filter(c => {
              if (!this.isAsaasChargePendingBoleto(c)) return false;
              const dd = c.dueDate ? String(c.dueDate).slice(0, 10) : '';
              if (!dd) return false;
              return dd >= startDay && dd <= endDay;
            });
            const ids = pendingBoletoInWindow.map(c => c.id).filter((id): id is string => !!id);
            return this.postBoletosCancelBatchQuiet(ids);
          }),
          catchError(() => of({ cancelledCount: 0 }))
        );
      }),
      catchError(() => of({ cancelledCount: 0 }))
    );
  }

  /** Só cobrança em aberto (PENDING) e, se a API enviar tipo, apenas boleto (não PIX/cartão). */
  private isAsaasChargePendingBoleto(c: AsaasPaymentResponse): boolean {
    const st = (c.status || '').toUpperCase().trim();
    if (st !== 'PENDING') return false;
    const bt = (c.billingType || '').toUpperCase().trim();
    if (!bt) return true;
    return bt === 'BOLETO' || bt === 'BOLETO_BANK_SLIP';
  }

  /** POST cancel-batch sem toast (fluxo congelamento). */
  private postBoletosCancelBatchQuiet(chargeIds: string[]): Observable<{ cancelledCount: number }> {
    if (!chargeIds.length) {
      return of({ cancelledCount: 0 });
    }
    return this.http
      .post<any>(this.buildUrl('/payments/asaas/boletos/cancel-batch'), { chargeIds })
      .pipe(
        map(() => ({ cancelledCount: chargeIds.length })),
        catchError(() => of({ cancelledCount: 0 }))
      );
  }

  private isoUtcToYyyyMmDd(iso: string): string | null {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  createBBBoleto(boleto: BBBoleto): Observable<any> {
    return this.http.post<any>(this.buildUrl('/payments/bb/boleto'), boleto)
      .pipe(catchError(err => this.handleError(err, 'Erro ao criar boleto BB')));
  }

  createBBBoletoRaw(payload: any): Observable<any> {
    return this.http.post<any>(this.buildUrl('/payments/bb/boleto/raw'), payload)
      .pipe(catchError(err => this.handleError(err, 'Erro ao criar boleto (raw) BB')));
  }

  getBBBoleto(nossoNumero: string): Observable<any> {
    return this.http.get<any>(this.buildUrl(`/payments/bb/boleto/${nossoNumero}`))
      .pipe(catchError(err => this.handleError(err, 'Erro ao obter boleto BB')));
  }

  // --- PIX ---

  createBBPix(pix: BBPix): Observable<any> {
    return this.http.post<any>(this.buildUrl('/payments/bb/pix'), pix)
      .pipe(catchError(err => this.handleError(err, 'Erro ao criar PIX BB')));
  }

  getBBPix(txid: string): Observable<any> {
    return this.http.get<any>(this.buildUrl(`/payments/bb/pix/${txid}`))
      .pipe(catchError(err => this.handleError(err, 'Erro ao obter PIX BB')));
  }

  uploadFile(file: File, prefix?: string): Observable<BlobUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    
    let params = new HttpParams();
    if (prefix) {
      params = params.set('prefix', prefix);
    }

    return this.http.post<BlobUploadResponse>(this.buildUrl('/blob/upload'), formData, { params })
      .pipe(catchError(err => this.handleError(err, 'Erro ao fazer upload de arquivo')));
  }

  downloadFile(fileName: string, prefix?: string): Observable<Blob> {
    let params = new HttpParams().set('fileName', fileName);
    if (prefix) {
      params = params.set('prefix', prefix);
    }

    return this.http.get(this.buildUrl('/blob/download'), { 
      params, 
      responseType: 'blob' 
    })
      .pipe(catchError(err => this.handleError(err, 'Erro ao baixar arquivo')));
  }

  listFiles(prefix?: string, maxResults: number = 100): Observable<any> {
    let params = new HttpParams().set('maxResults', maxResults.toString());
    if (prefix) {
      params = params.set('prefix', prefix);
    }

    return this.http.get<any>(this.buildUrl('/blob/list'), { params })
      .pipe(catchError(err => this.handleError(err, 'Erro ao listar arquivos')));
  }

  // ==========================================
  // 4. Test
  // ==========================================

  testApi(): Observable<any> {
    return this.http.get<any>(this.buildUrl('/api/Test'))
      .pipe(catchError(err => this.handleError(err, 'Erro ao testar API')));
  }

  private handleError(error: any, defaultMessage: string): Observable<never> {
    const message = this.extractApiErrorMessage(error) || defaultMessage;
    this.errorService.showErrorMessage(message);
    return throwError(() => error);
  }

  /**
   * Extrai a mensagem de erro amigável da resposta da API.
   * Suporta formato: { "message": "...", "error": "Body: {\"errors\":[{\"description\":\"...\"}]}" }
   */
  private extractApiErrorMessage(error: any): string | null {
    if (!error?.error) return null;

    const err = error.error;

    // 1) Array de erros direto (ex: errors[].description)
    if (Array.isArray(err.errors) && err.errors.length > 0 && err.errors[0].description) {
      return err.errors[0].description;
    }

    // 2) String em err.error (ex: "Failed to... Body: {\"errors\":[{\"description\":\"Não é permitido...\"}]}")
    const errorStr = typeof err.error === 'string' ? err.error : '';
    if (errorStr) {
      const bodyIndex = errorStr.indexOf('Body:');
      if (bodyIndex !== -1) {
        const jsonStr = errorStr.slice(bodyIndex + 5).trim().replace(/\\"/g, '"');
        try {
          const parsed = JSON.parse(jsonStr);
          if (parsed?.errors?.[0]?.description) {
            return parsed.errors[0].description;
          }
        } catch (_) {
          // ignora falha de parse
        }
      }
      // 3) Fallback: captura "description":"valor" na string (suporta \" no JSON)
      const descMatch = errorStr.match(/\\"description\\"\\s*:\\s*\\"((?:[^\\"]|\\.)*)\\"/)
        || errorStr.match(/"description"\s*:\s*"([^"]*)"/);
      if (descMatch && descMatch[1]) {
        return descMatch[1].replace(/\\"/g, '"');
      }
    }

    // 4) Mensagem genérica da API
    if (err.message) return err.message;

    return null;
  }
}
